import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Home, 
  CheckCircle, 
  FileText, 
  Users, 
  Star, 
  Shield, 
  Target, 
  ArrowRight,
  Calendar,
  MapPin,
  Award,
  Zap,
  Globe2,
  Building
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '@/styles/animations.css';

const PermanentResidenceCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      title: "Permanent Status",
      description: "Live in Poland indefinitely without time restrictions",
      icon: Home
    },
    {
      title: "Work Freedom",
      description: "Work in any profession without additional permits",
      icon: Users
    },
    {
      title: "EU Benefits",
      description: "Access to all EU social benefits and services",
      icon: Globe2
    },
    {
      title: "Family Rights",
      description: "Bring family members and sponsor their residence",
      icon: Building
    }
  ];

  const requirements = [
    "Continuous legal residence in Poland for at least 5 years",
    "Stable and regular income sufficient for living",
    "Health insurance coverage",
    "Adequate accommodation",
    "Clean criminal record",
    "Basic knowledge of Polish language (A1 level)",
    "Integration into Polish society"
  ];

  const process = [
    {
      step: "1",
      title: "Eligibility Check",
      description: "Verify you meet the 5-year residence requirement"
    },
    {
      step: "2",
      title: "Document Collection",
      description: "Gather all required documents and certificates"
    },
    {
      step: "3",
      title: "Language Test",
      description: "Take and pass the Polish language proficiency test"
    },
    {
      step: "4",
      title: "Application Submission",
      description: "Submit complete application to voivodeship office"
    },
    {
      step: "5",
      title: "Processing & Approval",
      description: "Wait for processing and collect your permanent card"
    }
  ];

  const benefits = [
    "Live in Poland indefinitely without time limits",
    "Work in any profession without work permits",
    "Access to all social benefits and healthcare",
    "Vote in local elections",
    "Bring family members to Poland",
    "Travel freely within the Schengen area",
    "Apply for Polish citizenship after 3 years",
    "Access to education and student benefits"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-green-500/5 via-green-600/10 to-background py-24 sm:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-green-500/10 text-green-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Home className="h-4 w-4" />
              Permanent Residence Card
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              Secure Your Future in Poland
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up mb-8">
              Secure your long-term future in Poland with our expert assistance in obtaining permanent residency. 
              We handle the complex documentation and requirements, ensuring your application meets all criteria for approval.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                  Start Application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Features Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Key Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              What you get with a Permanent Residence Card
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <CardHeader className="pb-4">
                    <div className="mx-auto bg-green-500/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-green-600" />
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground font-alumni">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground font-hind">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6 font-alumni">Requirements</h2>
              <p className="text-lg text-muted-foreground mb-8 font-hind">
                To qualify for a Permanent Residence Card, you must meet the following requirements:
              </p>
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground font-hind">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-green-50 rounded-2xl p-8">
              <div className="text-center">
                <div className="bg-green-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Target className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">Processing Time</h3>
                <p className="text-3xl font-bold text-green-600 mb-2">3-6 Months</p>
                <p className="text-muted-foreground font-hind">Average processing time from application to approval</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Application Process</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Our comprehensive 5-step process ensures your permanent residence application is successful
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-green-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-green-600">{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 font-alumni">{step.title}</h3>
                <p className="text-muted-foreground text-sm font-hind">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-green-500/5 to-green-600/10 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">What You Can Do</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
                With a Permanent Residence Card, you gain full rights and privileges in Poland
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground font-hind">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4 font-alumni">Ready to Secure Your Future?</h2>
            <p className="text-xl mb-8 opacity-90 font-hind">
              Take the next step towards permanent residence in Poland with our expert guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100">
                  Start Application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white !text-green-400 hover:bg-white/10 hover:!text-white"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PermanentResidenceCard; 